import './TextPropertyToolbar.scss';
import React, {useState} from 'react';
import {
  Box,
  FormControlLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material';
import {useTranslation} from 'react-i18next';
import ArrButton from '../../../../common/arr-button/ArrButton';
import ArrSingleSelectDropdown from '../../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../../shared/types';
import {ReactComponent as RadioActiveIcon} from '../../../../../assets/images/radio-active-icon.svg';
import {ReactComponent as RadioEnableIcon} from '../../../../../assets/images/radio-enable-icon.svg';
import {DEFAULT_FONT_SIZE, FieldType} from '../../CreateNewProductionUtils';
import InsertFieldPanel from '../insert-field-panel/InsertFieldPanel';
import ArrModal from '../../../../common/arr-modal/ArrModal';

type TextPropertyToolbarProps =
  | {
      variant?: 'with-options';
      title?: never;
      insertFieldInside?: 'modal' | 'flyout';
      fontFamily: string;
      fontStyle: string;
      fontSize: string;
      toolbarText: string;
      fontOptions: ISelectItem[];
      fontStyleOptions: ISelectItem[];
      fontSizeOptions: ISelectItem[];
      insertFieldTypes?: ISelectItem[];
      metadataFieldOptions?: ISelectItem[];
      productionFieldOptions?: ISelectItem[];
      onFontFamilyChange: (value: string) => void;
      onFontStyleChange: (value: string) => void;
      onFontSizeChange: (value: string) => void;
      onToolbarTextChange: (value: string) => void;
      onInsertField: (field: string | number) => void;
      onInsertFieldClick?: () => void;
      placeholder?: string;
      disabled?: boolean;
      dataTestId: string;
      initialToolbarData?: {
        hasContent: boolean;
        content: string;
        fontFamily: string;
        fontStyle: string;
        fontSize: string;
      };
      textAreaTestId?: string;
    }
  | {
      variant?: 'with-title';
      title: string;
      insertFieldInside?: 'modal' | 'flyout';
      fontFamily?: never;
      fontStyle?: never;
      fontSize?: never;
      toolbarText: string;
      fontOptions?: never;
      fontStyleOptions?: never;
      fontSizeOptions?: never;
      insertFieldTypes?: ISelectItem[];
      metadataFieldOptions?: ISelectItem[];
      productionFieldOptions?: ISelectItem[];
      onFontFamilyChange?: never;
      onFontStyleChange?: never;
      onFontSizeChange?: never;
      onToolbarTextChange: (value: string) => void;
      onInsertFieldClick?: () => void;
      onInsertField: (field: string | number) => void;
      placeholder?: string;
      disabled?: boolean;
      dataTestId: string;
      initialToolbarData?: {
        hasContent: boolean;
        content: string;
        fontFamily: never;
        fontStyle: never;
        fontSize: never;
      };
      textAreaTestId?: string;
    }
  | {
      variant?: 'with-only-title';
      title: string;
      insertFieldInside?: 'modal' | 'flyout';
      fontFamily?: never;
      fontStyle?: never;
      fontSize?: never;
      toolbarText: string;
      fontOptions?: never;
      fontStyleOptions?: never;
      fontSizeOptions?: never;
      insertFieldTypes?: never;
      metadataFieldOptions?: never;
      productionFieldOptions?: never;
      onFontFamilyChange?: never;
      onFontStyleChange?: never;
      onFontSizeChange?: never;
      onToolbarTextChange: (value: string) => void;
      onInsertFieldClick?: () => void;
      onInsertField?: never;
      placeholder?: string;
      disabled?: boolean;
      dataTestId: string;
      initialToolbarData?: {
        hasContent: boolean;
        content: string;
        fontFamily: never;
        fontStyle: never;
        fontSize: never;
      };
      textAreaTestId?: string;
    };

const TextPropertyToolbar: React.FC<TextPropertyToolbarProps> = ({
  variant = 'with-options',
  title = '',
  insertFieldInside,
  fontFamily,
  fontStyle,
  fontSize,
  toolbarText,
  fontOptions = [],
  fontStyleOptions = [],
  fontSizeOptions = [],
  insertFieldTypes = [],
  metadataFieldOptions = [],
  productionFieldOptions = [],
  onFontFamilyChange,
  onFontStyleChange,
  onFontSizeChange,
  onToolbarTextChange,
  onInsertField,
  onInsertFieldClick,
  placeholder,
  disabled = false,
  initialToolbarData: _initialToolabrData,
  dataTestId,
  textAreaTestId,
}) => {
  const {t} = useTranslation();

  // Get current field options based on selected type
  const getCurrentFieldOptions = (currentFieldType: string) => {
    return currentFieldType === FieldType.METADATA
      ? metadataFieldOptions || []
      : productionFieldOptions || [];
  };

  // Insert Field Modal state
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [showInsertFieldFlyout, setShowInsertFieldFlyout] =
    useState<boolean>(false);
  const [showInsertFieldModal, setShowInsertFieldModal] =
    useState<boolean>(false);
  const [fieldType, setFieldType] = useState<string>(FieldType.METADATA);
  const [selectedField, setSelectedField] = useState<string>(
    String(getCurrentFieldOptions(fieldType)[0]?.label || '')
  );

  // State to control the internal InsertFieldPanel
  const [isInsertFieldOpen, setIsInsertFieldOpen] = useState(false);
  const defaultFontSize = Number(DEFAULT_FONT_SIZE);

  // options now come from props

  // Insert Field Modal handlers
  const handleInsertFieldOpen = () => {
    if (onInsertFieldClick) {
      onInsertFieldClick();
    } else if (insertFieldInside === 'modal') {
      setShowInsertFieldModal(true);
    } else {
      // Default behavior: open the internal panel
      setIsInsertFieldOpen(true);
    }
  };
  const handleInsertFieldClose = () => {
    setShowInsertFieldFlyout(false);
    setShowInsertFieldModal(false);
    setSelectedField(
      String(getCurrentFieldOptions(FieldType.METADATA)[0]?.label || '')
    ); // Reset to default
    setFieldType(FieldType.METADATA); // Reset to default
  };

  const handleInsertFieldConfirm = () => {
    if (selectedField) {
      onInsertField?.(selectedField);
      handleInsertFieldClose();
    }
  };

  const handleFieldTypeChange = (value: string) => {
    setFieldType(value);
    // Reset selected field when type changes
    setSelectedField(String(getCurrentFieldOptions(value)[0]?.label || ''));
  };

  const handleFieldSelectionChange = (value: string) => {
    setSelectedField(value);
  };

  return (
    <Box className="text-property-toolbar">
      <Box className="text-property-toolbar-header">
        {variant == 'with-options' && (
          <Box className="text-styling">
            <ArrSingleSelectDropdown
              dropdownLabel={
                String(fontOptions?.[0]?.label) ||
                t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.fontOptions.arial'
                )
              }
              dropdownItems={fontOptions || []}
              dropdownItemKey="value"
              dropdownItemDisplay="label"
              selectedValues={
                fontOptions?.find((item) => item.value === fontFamily) ||
                fontOptions?.[0] ||
                null
              }
              onSelectionChange={(item) => {
                if (disabled) return;
                if (item?.value && onFontFamilyChange) {
                  onFontFamilyChange(String(item.value));
                }
              }}
              sizeVariant="small"
              customWidth={'8rem'}
              disabled={disabled}
              dataTestId="asd-font-option-arial"
            />
            <ArrSingleSelectDropdown
              dropdownLabel={
                String(fontStyleOptions?.[0]?.label) ||
                t(
                  'productionsAndExports.createNewProduction.productionComponents.tiffPdfComponent.fontOptions.regular'
                )
              }
              dropdownItems={fontStyleOptions || []}
              dropdownItemKey="value"
              dropdownItemDisplay="label"
              selectedValues={
                fontStyleOptions?.find((item) => item.value === fontStyle) ||
                fontStyleOptions?.[0] ||
                null
              }
              onSelectionChange={(item) => {
                if (disabled) return;
                if (item?.value && onFontStyleChange) {
                  onFontStyleChange(String(item.value));
                }
              }}
              sizeVariant="small"
              customWidth={'6rem'}
              disabled={disabled}
              dataTestId="asd-font-option-regular"
            />
            <ArrSingleSelectDropdown
              dropdownLabel={
                String(fontSizeOptions?.[0]?.label) || String(defaultFontSize)
              }
              dropdownItems={fontSizeOptions || []}
              dropdownItemKey="value"
              dropdownItemDisplay="label"
              selectedValues={
                fontSizeOptions?.find((item) => item.value === fontSize) ||
                fontSizeOptions?.[0] ||
                null
              }
              onSelectionChange={(item) => {
                if (disabled) return;
                if (item?.value && onFontSizeChange) {
                  onFontSizeChange(String(item.value));
                }
              }}
              sizeVariant="small"
              customWidth={'4rem'}
              disabled={disabled}
              dataTestId="asd-default-font-size"
            />
          </Box>
        )}
        {(variant == 'with-title' || variant == 'with-only-title') && (
          <Typography
            className="header-title typo-text subhead5_size"
            data-testid={`text-property-toolbar-title-${dataTestId}`}
          >
            {title}
          </Typography>
        )}
        {(variant == 'with-title' || variant == 'with-options') && (
          <ArrButton
            variant="primary"
            size="small"
            onClick={handleInsertFieldOpen}
            className="insert-field-btn"
            disabled={disabled}
            dataTestId={`text-property-toolbar-insert-field-button-text-button-${dataTestId}`}
          >
            {t(
              'productionsAndExports.createNewProduction.common.toolbar.insertFieldButtonText'
            )}
          </ArrButton>
        )}
      </Box>
      {/* Branding Text Input */}
      <Box className="branding-text-input">
        <TextField
          className="branding-text-field typo_text body2_size"
          placeholder={placeholder}
          value={toolbarText}
          onChange={(e) => {
            if (disabled) return;
            onToolbarTextChange(e.target.value);
          }}
          multiline
          minRows={2}
          disabled={disabled}
          slotProps={{
            htmlInput: {
              'data-testid': `${(textAreaTestId ?? placeholder)?.replaceAll(' ', '-')}-input`,
            },
          }}
        />
      </Box>
      {/* Insert Field Modal */}
      <ArrModal
        showModal={showInsertFieldModal}
        headerTitle={t(
          'productionsAndExports.createNewProduction.common.toolbar.insertFieldTitle'
        )}
        onClose={handleInsertFieldClose}
        className="insert-field-modal"
        headerTitleClassName="headline3_size insert-field-header-title"
        showClose={false}
        dataTestId="insert-field-modal"
        buttons={[
          {
            id: 'cancel',
            label: t('common.cancel'),
            variant: 'secondary',
            onClick: handleInsertFieldClose,
            dataTestId: 'ifm-cancel-btn',
          },
          {
            id: 'ok',
            label: t('common.select'),
            variant: 'primary',
            onClick: handleInsertFieldConfirm,
            disabled: !selectedField,
            dataTestId: 'ifm-select-btn',
          },
        ]}
      >
        <Box className="insert-field-content">
          {/* Insert Field Method Radio Buttons */}
          <Box className="insert-field-type-section">
            <RadioGroup
              row
              value={fieldType}
              onChange={(e) => {
                const selectedObj = insertFieldTypes?.find(
                  (opt) => opt.value === e.target.value
                );
                if (selectedObj)
                  handleFieldTypeChange(
                    String(selectedObj.value) || FieldType.METADATA
                  );
              }}
              className="custom-radio-group typo-text body3_text"
              aria-label={t(
                'productionsAndExports.createNewProduction.common.toolbar.insertFieldTitle'
              )}
            >
              {insertFieldTypes?.map(({value, label}) => {
                return (
                  <FormControlLabel
                    data-testid={`insert-field-types-${value}`}
                    key={value}
                    value={value}
                    control={
                      <Radio
                        icon={
                          <RadioEnableIcon data-testid="insert-field-types-radio-enable-icon" />
                        }
                        checkedIcon={
                          <RadioActiveIcon data-testid="insert-field-types-radio-active-icon" />
                        }
                        slotProps={{
                          input: {
                            'aria-label': String(label),
                            'data-testid': `radio-${value}`,
                          } as React.InputHTMLAttributes<HTMLInputElement>,
                        }}
                      />
                    }
                    label={label}
                  />
                );
              })}
            </RadioGroup>
          </Box>

          <Box className="insert-field-section">
            <ArrSingleSelectDropdown
              dropdownLabel={
                String(getCurrentFieldOptions(fieldType)?.[0]?.label) ||
                t(
                  'productionsAndExports.createNewProduction.common.toolbar.selectFieldLabel'
                )
              }
              dropdownItems={getCurrentFieldOptions(fieldType) || []}
              dropdownItemKey="value"
              dropdownItemDisplay="label"
              selectedValues={
                getCurrentFieldOptions(fieldType)?.find(
                  (item) => item.value === selectedField
                ) || null
              }
              onSelectionChange={(item) => {
                if (item?.value) {
                  handleFieldSelectionChange(String(item.value));
                }
              }}
              sizeVariant="medium"
              searchable
              dataTestId="asd-text-property-select-field"
            />
          </Box>
        </Box>
      </ArrModal>
      {/* The toolbar now manages its own flyout panel by default */}
      {!onInsertFieldClick && (
        <InsertFieldPanel
          isOpen={isInsertFieldOpen}
          onClose={() => setIsInsertFieldOpen(false)}
          onInsert={(field) => {
            onInsertField?.(field);
            setIsInsertFieldOpen(false);
          }}
          insertFieldTypes={insertFieldTypes || []}
          metadataFieldOptions={metadataFieldOptions || []}
          productionFieldOptions={productionFieldOptions || []}
        />
      )}
    </Box>
  );
};

export default TextPropertyToolbar;
